import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import type { BillingUsageItem } from '@/lib/billing';
import {
  USAGE_LIMIT_LABEL,
  usageMeterClass,
  usageMeterValue,
} from './usage-nudge';

type Props = {
  item: BillingUsageItem;
  className?: string;
  title?: string;
};

/**
 * One limit's progress bar, tinted by the server's tier. Shared by the sidebar
 * `PlanCardUsage` and the billing page's `UsageCard` — the two render different
 * chrome around it but the bar itself must read identically in both, so it is
 * one component rather than two `Progress` call sites that would drift.
 *
 * Built on `components/ui/progress` (Radix) so `role="progressbar"` and the
 * aria value come from the primitive. The value is the SERVER's `pct`, never
 * `used / (used + remaining)`: `remaining` is live enforcement headroom from a
 * different store and does not sum with `used` to `limit`.
 *
 * An unlimited limit renders NO bar at all. A full bar would be a lie and an
 * empty one would read as "0 of 0" — there is simply nothing to meter, so the
 * caller shows an "Unlimited" label in its place.
 */
export function UsageMeter({ item, className, title }: Props) {
  if (item.limit === null) return null;

  return (
    <Progress
      value={usageMeterValue(item)}
      aria-label={`${USAGE_LIMIT_LABEL[item.code]} usage`}
      title={title}
      className={cn(
        'h-1.5 bg-[color:var(--ink-200)]',
        usageMeterClass(item),
        className,
      )}
    />
  );
}
